/**
 * Review Page - Complete Suggestion Review Workflow
 */

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Chip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  CheckCircle as SuccessIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { EntrySuggestion, SuggestionResponse, MessageGap } from '@/types/api';
import {
  useGenerateSuggestions,
  useBulkAcceptSuggestions,
  useReparseSuggestions,
} from '@/hooks/useSuggestions';
import { streamSuggestions } from '@/api/suggestions';
import { useLibraries } from '@/hooks/useLibraries';
import { useManuscriptChats } from '@/hooks/useMessages';
import { useChatLabel } from '@/hooks/useChatLabel';
import SuggestionList from '@/components/suggestions/SuggestionList';
import SuggestionReviewDialog from '@/components/suggestions/SuggestionReviewDialog';
import BulkActionToolbar from '@/components/suggestions/BulkActionToolbar';
import BulkEditDialog, { BulkEditValues } from '@/components/suggestions/BulkEditDialog';
// LoadingSpinner available if needed
// import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import ContextPanel from '@/components/messages/ContextPanel';
import ProgressTracker from '@/components/review/ProgressTracker';
import SessionManager from '@/components/review/SessionManager';
import MissingEntryDialog from '@/components/entries/MissingEntryDialog';
import {
  useStartSession,
  useUpdateProgress,
} from '@/hooks/useReviewProgress';
import { ReviewSessionSummary } from '@/types/api';

const MAX_MESSAGE_RANGE = 2000;

const ReviewPage: React.FC = () => {
  const { t } = useTranslation(['review', 'common']);
  const chatLabel = useChatLabel();
  // State for filters and range
  const [chatId, setChatId] = useState<number | null>(null); // Default to first chat when available
  const [startMessageId, setStartMessageId] = useState<string>('');
  const [numberOfMessages, setNumberOfMessages] = useState<string>('');
  const [minConfidence, setMinConfidence] = useState<number>(0.5);

  // State for context panel
  const [contextPanelOpen, setContextPanelOpen] = useState(true);
  const [contextMessageId, setContextMessageId] = useState<number | null>(null);
  const [contextChatId, setContextChatId] = useState<number | null>(null);

  // State for suggestions and gaps
  const [suggestions, setSuggestions] = useState<EntrySuggestion[]>([]);
  const [gaps, setGaps] = useState<MessageGap[]>([]);
  const [suggestionResponse, setSuggestionResponse] = useState<SuggestionResponse | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  // State for missing entry dialog (for gaps)
  const [missingEntryDialogOpen, setMissingEntryDialogOpen] = useState(false);
  const [missingEntryLibraryId, setMissingEntryLibraryId] = useState<number | null>(null);
  const [missingEntryMsNo, setMissingEntryMsNo] = useState<string>('');

  // State for review dialog
  const [selectedSuggestion, setSelectedSuggestion] = useState<EntrySuggestion | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<Set<number>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // State for review progress
  const [reviewedCount, setReviewedCount] = useState(0);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  // State for progress tracking session
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  // Hooks
  const generateMutation = useGenerateSuggestions();
  const bulkAcceptMutation = useBulkAcceptSuggestions();
  const reparseMutation = useReparseSuggestions();
  const { data: libraries = [], refetch: refetchLibraries } = useLibraries();
  const { data: chats = [], isLoading: chatsLoading } = useManuscriptChats();

  // Progress tracking hooks
  const startSessionMutation = useStartSession();
  const updateProgressMutation = useUpdateProgress();

  // Ref for streaming cleanup function
  const streamCleanupRef = useRef<(() => void) | null>(null);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if (chatId === null && chats.length > 0) {
      setChatId(chats[0].id);
    }
  }, [chatId, chats]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    if (!currentSessionId) return;

    const interval = setInterval(() => {
      updateProgressMutation.mutate({
        sessionId: currentSessionId,
        data: {
          reviewed_messages: reviewedCount,
          accepted_count: acceptedCount,
          rejected_count: rejectedCount,
        },
      });
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [currentSessionId, reviewedCount, acceptedCount, rejectedCount, updateProgressMutation]);

  const handleGenerate = async () => {
    if (!chatId) {
      alert(t('generate.errors.noChat'));
      return;
    }

    const start = parseInt(startMessageId, 10);
    const numMessages = parseInt(numberOfMessages, 10);

    if (Number.isNaN(start) || Number.isNaN(numMessages)) {
      alert(t('generate.errors.invalidInputs'));
      return;
    }

    if (numMessages <= 0) {
      alert(t('generate.errors.invalidNumberOfMessages'));
      return;
    }

    const totalMessages = numMessages;
    const isChunking = totalMessages > MAX_MESSAGE_RANGE;

    setIsGenerating(true);

    try {
      let allSuggestions: EntrySuggestion[] = [];
      let combinedResponse: SuggestionResponse | null = null;

      let allGaps: MessageGap[] = [];

      if (isChunking) {
        let processedMessages = 0;
        let remainingMessages = totalMessages;
        let currentStart = start;

        while (remainingMessages > 0) {
          const chunkSize = Math.min(MAX_MESSAGE_RANGE, remainingMessages);

          const response = await generateMutation.mutateAsync({
            chat_id: chatId,
            start_message_id: currentStart,
            end_message_id: currentStart + chunkSize - 1,
            min_confidence: minConfidence,
          });

          allSuggestions = [...allSuggestions, ...response.suggestions];
          allGaps = [...allGaps, ...response.gaps];

          if (!combinedResponse) {
            combinedResponse = response;
          } else {
            combinedResponse.suggestions = allSuggestions;
            combinedResponse.gaps = allGaps;
            // Aggregate stats
            combinedResponse.stats.avg_confidence = (combinedResponse.stats.avg_confidence * (processedMessages) + response.stats.avg_confidence * chunkSize) / (processedMessages + chunkSize);
            combinedResponse.stats.with_library_match += response.stats.with_library_match;
            combinedResponse.stats.without_library_match += response.stats.without_library_match;
          }

          processedMessages += chunkSize;
          remainingMessages -= chunkSize;
          currentStart += chunkSize;
          setGenerationProgress(Math.round((processedMessages / totalMessages) * 100));
        }
      } else {
        // Use streaming for single-chunk requests
        setSuggestions([]);
        setGaps([]);
        setGenerationProgress(0);

        // Clean up any existing stream
        if (streamCleanupRef.current) {
          streamCleanupRef.current();
          streamCleanupRef.current = null;
        }

        // Stream suggestions
        await new Promise<void>((resolve, reject) => {
          streamCleanupRef.current = streamSuggestions(
            {
              chat_id: chatId,
              start_message_id: start,
              end_message_id: start + numMessages - 1,
              min_confidence: minConfidence,
            },
            {
              onSuggestion: (suggestion) => {
                setSuggestions((prev) => [...prev, suggestion]);
                allSuggestions.push(suggestion);
              },
              onGap: (gap) => {
                setGaps((prev) => [...prev, gap]);
                allGaps.push(gap);
              },
              onProgress: (data) => {
                setGenerationProgress(data.percentage);
              },
              onComplete: (stats) => {
                // Build combined response
                combinedResponse = {
                  chat_id: chatId,
                  start_message_id: start,
                  end_message_id: start + numMessages - 1,
                  min_confidence: minConfidence,
                  suggestions: allSuggestions,
                  gaps: allGaps,
                  stats: {
                    total_suggestions: stats.total_suggestions,
                    avg_confidence: stats.avg_confidence,
                    by_pattern: stats.by_pattern,
                    with_library_match: stats.with_library_match,
                    without_library_match: stats.without_library_match,
                  },
                };
                setGenerationProgress(0);
                resolve();
              },
              onError: (error) => {
                console.error('Streaming error:', error);
                alert(`Failed to generate suggestions: ${error.message}`);
                setGenerationProgress(0);
                reject(new Error(error.message));
              },
            }
          );
        });
      }

      setSuggestionResponse(combinedResponse);
      setSuggestions(allSuggestions);
      setGaps(allGaps);
      setReviewedCount(0);
      setAcceptedCount(0);
      setRejectedCount(0);

      // Start a progress tracking session
      if (combinedResponse) {
        try {
          const session = await startSessionMutation.mutateAsync({
            chat_id: chatId,
            start_message_id: start,
            number_of_messages: numMessages,
            session_metadata: {
              min_confidence: minConfidence,
              total_suggestions: allSuggestions.length,
            },
          });
          setCurrentSessionId(session.id);
          setSessionStartTime(new Date(session.created_at));
        } catch (error) {
          console.error('Failed to start progress session:', error);
          // Don't fail the whole operation if session creation fails
        }
      }
    } catch (error) {
      console.error('Failed to generate suggestions:', error);
    } finally {
      if (isChunking) {
        setGenerationProgress(0);
      }
      setIsGenerating(false);
    }
  };

  const handleReview = useCallback((suggestion: EntrySuggestion) => {
    setSelectedSuggestion(suggestion);
    setDialogOpen(true);
  }, []);

  const handleAccept = () => {
    setReviewedCount((prev) => prev + 1);
    setAcceptedCount((prev) => prev + 1);

    // Remove accepted suggestion from list
    setSuggestions((prev) =>
      prev.filter((s) => s.title_message_id !== selectedSuggestion?.title_message_id)
    );
    setSelectedSuggestionIds((prev) => {
      const next = new Set(prev);
      if (selectedSuggestion?.title_message_id) {
        next.delete(selectedSuggestion.title_message_id);
      }
      return next;
    });

    setDialogOpen(false);
    setSelectedSuggestion(null);
  };

  const handleReject = () => {
    setReviewedCount((prev) => prev + 1);
    setRejectedCount((prev) => prev + 1);

    // Remove rejected suggestion from list
    setSuggestions((prev) =>
      prev.filter((s) => s.title_message_id !== selectedSuggestion?.title_message_id)
    );
    setSelectedSuggestionIds((prev) => {
      const next = new Set(prev);
      if (selectedSuggestion?.title_message_id) {
        next.delete(selectedSuggestion.title_message_id);
      }
      return next;
    });

    setDialogOpen(false);
    setSelectedSuggestion(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSuggestion(null);
  };

  // Progress tracking handlers
  const handleResumeSession = async (session: ReviewSessionSummary) => {
    // Set the chat and message range
    setChatId(session.chat_id);
    setStartMessageId(session.start_message_id.toString());

    // Calculate number of messages from stored end_message_id
    // (for backward compatibility with existing sessions)
    const numMessages = session.end_message_id - session.start_message_id + 1;
    setNumberOfMessages(numMessages.toString());

    // Restore the session
    setCurrentSessionId(session.id);
    setReviewedCount(session.progress_percentage > 0 ? Math.floor((session.progress_percentage / 100) * (session.end_message_id - session.start_message_id + 1)) : 0);
    setAcceptedCount(session.accepted_count);
    setRejectedCount(session.rejected_count);

    // Re-generate suggestions for the same range
    try {
      const response = await generateMutation.mutateAsync({
        chat_id: session.chat_id,
        start_message_id: session.start_message_id,
        end_message_id: session.end_message_id,
        min_confidence: 0.5,
      });
      setSuggestionResponse(response);
      setSuggestions(response.suggestions);
      setGaps(response.gaps);
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  };

  // Context panel handler
  const handleViewMessage = useCallback((messageId: number, msgChatId: number) => {
    setContextMessageId(messageId);
    setContextChatId(msgChatId);
    setContextPanelOpen(true);
  }, []);

  // Stable callback for viewing context from suggestion cards
  const handleViewContextFromCard = useCallback((messageId: number) => {
    if (chatId) {
      setContextMessageId(messageId);
      setContextChatId(chatId);
      setContextPanelOpen(true);
    }
  }, [chatId]);

  const handleToggleSuggestion = useCallback((messageId: number) => {
    setSelectedSuggestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  const handleClearSelection = () => {
    setSelectedSuggestionIds(new Set());
  };

  const handleSelectAll = () => {
    if (selectedSuggestionIds.size === suggestions.length) {
      // All are selected, deselect all
      setSelectedSuggestionIds(new Set());
    } else {
      // Select all
      setSelectedSuggestionIds(new Set(suggestions.map(s => s.title_message_id)));
    }
  };

  const handleBulkAccept = async () => {
    if (!chatId) {
      alert(t('generate.errors.noChat'));
      return;
    }

    const selectedSuggestions = suggestions.filter((s) =>
      selectedSuggestionIds.has(s.title_message_id)
    );

    if (selectedSuggestions.length === 0) {
      return;
    }

    try {
      const response = await bulkAcceptMutation.mutateAsync({
        chat_id: chatId,
        suggestions: selectedSuggestions,
      });

      const failedIds = new Set(
        response.failed.map((failure) => failure.suggestion_message_id)
      );
      const acceptedSuggestions = selectedSuggestions.filter(
        (s) => !failedIds.has(s.title_message_id)
      );

      if (acceptedSuggestions.length > 0) {
        const acceptedIds = new Set(
          acceptedSuggestions.map((suggestion) => suggestion.title_message_id)
        );
        setReviewedCount((prev) => prev + acceptedSuggestions.length);
        setAcceptedCount((prev) => prev + acceptedSuggestions.length);
        setSuggestions((prev) => prev.filter((s) => !acceptedIds.has(s.title_message_id)));
      }

      setSelectedSuggestionIds(new Set());

      if (response.failed.length > 0) {
        setSnackbarMessage(
          t('bulkActions.acceptFailed', {
            accepted: acceptedSuggestions.length,
            failed: response.failed.length,
          })
        );
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Bulk accept failed:', error);
      setSnackbarMessage(t('bulkActions.acceptFailedGeneric'));
      setSnackbarOpen(true);
    }
  };

  const handleOpenBulkEdit = () => {
    if (selectedSuggestionIds.size === 0) {
      return;
    }
    setBulkEditOpen(true);
  };

  const handleBulkEditApply = async (values: BulkEditValues) => {
    const targetIds = new Set(selectedSuggestionIds);
    if (targetIds.size === 0) {
      setBulkEditOpen(false);
      return;
    }

    let updatedSuggestions = suggestions;

    if (values.reparse) {
      if (!chatId || (!values.pattern && !values.customPattern)) {
        alert(t('generate.errors.noChat'));
      } else {
        try {
          const response = await reparseMutation.mutateAsync({
            chat_id: chatId,
            message_ids: Array.from(targetIds),
            pattern: values.pattern,
            custom_pattern: values.customPattern,
            field_mapping: values.fieldMapping,
          });

          const replacementMap = new Map(
            response.suggestions.map((suggestion) => [suggestion.title_message_id, suggestion])
          );

          if (replacementMap.size < targetIds.size) {
            setSnackbarMessage(
              t('bulkActions.reparseUpdated', {
                updated: replacementMap.size,
                total: targetIds.size,
              })
            );
            setSnackbarOpen(true);
          }

          updatedSuggestions = updatedSuggestions.map((suggestion) =>
            replacementMap.get(suggestion.title_message_id) || suggestion
          );
        } catch (error) {
          console.error('Failed to reparse suggestions:', error);
          setSnackbarMessage(t('bulkActions.reparseFailed'));
          setSnackbarOpen(true);
        }
      }
    }

    if (values.libraryId || values.author) {
      updatedSuggestions = updatedSuggestions.map((suggestion) => {
        if (!targetIds.has(suggestion.title_message_id)) {
          return suggestion;
        }

        const next = { ...suggestion };

        if (values.author !== undefined) {
          next.author = values.author;
        }

        if (values.libraryId) {
          const selectedLibrary = libraries.find((lib) => lib.id === values.libraryId);
          next.library_matches = [
            {
              library_id: values.libraryId,
              library_name: selectedLibrary?.canonical_name || `Library ${values.libraryId}`,
              match_type: 'manual',
              confidence: 1.0,
            },
          ];
        }

        return next;
      });
    }

    setSuggestions(updatedSuggestions);
    setBulkEditOpen(false);
  };

  // Handler for creating missing entry from gap
  const handleCreateMissingFromGap = (
    _gap: MessageGap,
    inferredMsNo: string | null,
    inferredLibrary: number | null
  ) => {
    setMissingEntryLibraryId(inferredLibrary);
    setMissingEntryMsNo(inferredMsNo || '');
    setMissingEntryDialogOpen(true);
  };

  const handleMissingEntryCreated = () => {
    setMissingEntryDialogOpen(false);
    setMissingEntryLibraryId(null);
    setMissingEntryMsNo('');
    setSnackbarMessage(t('gaps.missingEntryCreated', 'Missing entry created successfully'));
    setSnackbarOpen(true);
  };

  // Handler for entry created via insert dialog
  const handleEntryCreatedFromInsert = () => {
    setAcceptedCount((prev) => prev + 1);
    setSnackbarMessage(t('entryCreated', 'Entry created successfully'));
    setSnackbarOpen(true);
  };

  const totalSuggestions = suggestionResponse?.suggestions.length || 0;
  const remainingSuggestions = suggestions.length;
  const selectedSuggestionIdList = useMemo(
    () => Array.from(selectedSuggestionIds).sort((a, b) => a - b),
    [selectedSuggestionIds]
  );
  const progressPercentage = totalSuggestions > 0
    ? ((reviewedCount / totalSuggestions) * 100)
    : 0;

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Main Content Area */}
      <Box
        sx={{
          flex: contextPanelOpen ? '0 0 calc(100% - 370px)' : 1,
          overflowY: 'auto',
          p: 3,
          transition: 'flex 0.3s ease',
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t('title')}
        </Typography>

        {/* Active Sessions Manager */}
        <SessionManager chatId={chatId ?? undefined} onResumeSession={handleResumeSession} />

        {/* Progress Tracker */}
        {currentSessionId && sessionStartTime && suggestionResponse && (
          <ProgressTracker
            totalMessages={suggestionResponse.suggestions.length}
            reviewedMessages={reviewedCount}
            acceptedCount={acceptedCount}
            rejectedCount={rejectedCount}
            sessionStartTime={sessionStartTime}
          />
        )}

      {/* Filters and Generate Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('generate.title')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={chatsLoading || chats.length === 0}>
              <InputLabel>{t('generate.chatLabel')}</InputLabel>
              <Select
                value={chatId ?? ''}
                onChange={(e) => setChatId(Number(e.target.value))}
                label={t('generate.chatLabel')}
                displayEmpty
              >
                {chats.map((chat) => (
                  <MenuItem key={chat.id} value={chat.id}>
                    {chatLabel(chat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('generate.startMessageIdLabel')}
              type="number"
              fullWidth
              value={startMessageId}
              onChange={(e) => setStartMessageId(e.target.value)}
              placeholder="e.g., 100"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('generate.numberOfMessagesLabel')}
              type="number"
              fullWidth
              value={numberOfMessages}
              onChange={(e) => setNumberOfMessages(e.target.value)}
              placeholder="e.g., 2000"
              helperText={t('generate.numberOfMessagesHelp')}
              InputProps={{
                inputProps: { min: 1 }
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('generate.minConfidenceLabel')}</InputLabel>
              <Select
                value={minConfidence}
                onChange={(e) => setMinConfidence(e.target.value as number)}
                label={t('generate.minConfidenceLabel')}
              >
                <MenuItem value={0.0}>{t('generate.confidenceLevels.all')}</MenuItem>
                <MenuItem value={0.5}>{t('generate.confidenceLevels.medium')}</MenuItem>
                <MenuItem value={0.7}>{t('generate.confidenceLevels.high')}</MenuItem>
                <MenuItem value={0.85}>{t('generate.confidenceLevels.veryHigh')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
                onClick={handleGenerate}
                disabled={
                  isGenerating ||
                  !chatId ||
                  !startMessageId ||
                  !numberOfMessages ||
                  chats.length === 0
                }
                fullWidth
                sx={{
                  opacity: isGenerating ? 0.8 : 1,
                  cursor: isGenerating ? 'wait' : 'pointer',
                }}
              >
                {isGenerating
                  ? generationProgress > 0
                    ? t('generate.buttonGeneratingProgress', { progress: generationProgress })
                    : t('generate.buttonGenerating')
                  : t('generate.button')}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {generateMutation.isError && (
          <ErrorAlert error={generateMutation.error} title={t('generate.errors.generationFailed')} />
        )}

        {/* Loading overlay */}
        {isGenerating && (
          <Box
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: 'action.hover',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              {generationProgress > 0
                ? t('generate.buttonGeneratingProgress', { progress: generationProgress })
                : t('loading')}
            </Typography>
            {generationProgress > 0 && (
              <LinearProgress
                variant="determinate"
                value={generationProgress}
                sx={{ width: '100%', height: 8, borderRadius: 4 }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Progress Section */}
      {totalSuggestions > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('progress.title')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<SuccessIcon />}
                label={t('progress.accepted', { count: acceptedCount })}
                color="success"
                size="small"
              />
              <Chip
                icon={<RejectIcon />}
                label={t('progress.rejected', { count: rejectedCount })}
                color="error"
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('progress.reviewedOfTotal', {
                reviewed: reviewedCount,
                total: totalSuggestions,
                remaining: remainingSuggestions,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {suggestionResponse && suggestionResponse.stats && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('progress.avgConfidence', {
                  confidence: (suggestionResponse.stats.avg_confidence * 100).toFixed(0),
                })}{' '}
                • {t('progress.withLibraryMatch', { count: suggestionResponse.stats.with_library_match })} •{' '}
                {t('progress.withoutLibraryMatch', { count: suggestionResponse.stats.without_library_match })}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Suggestions List */}
      {!isGenerating && suggestions.length > 0 && (
        <>
          <SuggestionList
            suggestions={suggestions}
            gaps={gaps}
            chatId={chatId!}
            selectedIds={selectedSuggestionIds}
            onToggleSelect={handleToggleSuggestion}
            onReview={handleReview}
            onViewContext={handleViewContextFromCard}
            onCreateMissingFromGap={handleCreateMissingFromGap}
            libraryId={null}
            onEntryCreated={handleEntryCreatedFromInsert}
          />
          <BulkActionToolbar
            selectedCount={selectedSuggestionIds.size}
            totalCount={suggestions.length}
            allSelected={selectedSuggestionIds.size === suggestions.length && suggestions.length > 0}
            onSelectAll={handleSelectAll}
            onBulkEdit={handleOpenBulkEdit}
            onBulkAccept={handleBulkAccept}
            onClearSelection={handleClearSelection}
            isSubmitting={bulkAcceptMutation.isPending}
          />
        </>
      )}

      {/* All suggestions reviewed */}
      {!isGenerating && totalSuggestions > 0 && remainingSuggestions === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            {t('allReviewed.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('allReviewed.stats', { accepted: acceptedCount, rejected: rejectedCount })}
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => {
              setSuggestions([]);
              setSuggestionResponse(null);
              setReviewedCount(0);
              setAcceptedCount(0);
              setRejectedCount(0);
              setNumberOfMessages('');
            }}
          >
            {t('allReviewed.newButton')}
          </Button>
        </Paper>
      )}

      {/* Review Dialog */}
      {chatId && (
        <SuggestionReviewDialog
          open={dialogOpen}
          suggestion={selectedSuggestion}
          chatId={chatId}
          libraries={libraries}
          mode="edit"
          onRefreshLibraries={refetchLibraries}
          onClose={handleCloseDialog}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}

      <BulkEditDialog
        open={bulkEditOpen}
        selectedCount={selectedSuggestionIds.size}
        selectedMessageIds={selectedSuggestionIdList}
        chatId={chatId}
        libraries={libraries}
        onRefreshLibraries={refetchLibraries}
        onClose={() => setBulkEditOpen(false)}
        onApply={handleBulkEditApply}
      />

      {/* Status Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Missing Entry Dialog (for gaps) */}
      {missingEntryLibraryId && (
        <MissingEntryDialog
          open={missingEntryDialogOpen}
          libraryId={missingEntryLibraryId}
          existingEntries={[]}
          mode="create"
          initialMsNo={missingEntryMsNo}
          onClose={() => {
            setMissingEntryDialogOpen(false);
            setMissingEntryLibraryId(null);
            setMissingEntryMsNo('');
          }}
          onCreated={handleMissingEntryCreated}
        />
      )}

      </Box>

      {/* Context Panel */}
      {contextPanelOpen && (
        <ContextPanel
          open={contextPanelOpen}
          chatId={contextChatId ?? chatId}
          anchorId={contextMessageId}
          suggestions={suggestions}
          onClose={() => setContextPanelOpen(false)}
          onJumpToId={(messageId) => {
            const targetChatId = contextChatId ?? chatId;
            if (targetChatId) {
              handleViewMessage(messageId, targetChatId);
            }
          }}
        />
      )}
    </Box>
  );
};

export default ReviewPage;
